import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _typeof from "@babel/runtime/helpers/typeof";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.object.keys.js";
/**
 * 通知权限引导 - 公共工具
 * 文档：Push 精细化分类通知权限引导 - 方案设计
 *
 * 功能：
 * 1. getNotificationGuideConfig  - 统一获取 Lion 配置（maoyan-show-m-web.frontEnd.config.projectDetailPageConfig.notificationGuide）
 * 2. checkNotificationPermission - 使用 KNB.requestPermission 检查通知权限（readonly 模式，仅猫眼/美团/大众点评三渠道）
 * 3. recordDismiss               - 记录用户关闭引导弹窗/横幅的时间戳（冷却计时）
 * 4. isInCoolingPeriod           - 判断当前 sceneType 是否仍在冷却期内
 * 5. openAppSetting              - 跳转系统设置页（iOS 三层备选，Android 直接用第一层）
 */

import Drip from 'drip';
import KNB from '@dp/knb';
import { getLionConfig } from '@/common/lionConfig';

/**
 * 频次控制存储 key
 * drip/store 读写时会自动加 _myshow_ 前缀，此处直接调用 KNB API 需手动带前缀
 * 落盘 key 与 MRN 端（maoyan-show-main-mrn）保持一致：_myshow_notification_guide_dismiss
 *
 * 存储数据结构示例（JSON）：
 * {
 *   "wish":       { "dismissTime": 1713871200000 }, // 许愿场景：用户关闭/确认引导的时间戳
 *   "preset":     { "dismissTime": 1713900000000 }, // 预约场景：用户关闭/确认引导的时间戳
 *   "outOfStock": { "dismissTime": 1713920000000 }, // 缺货登记场景：用户关闭/确认引导的时间戳
 *   "order":      { "dismissTime": 1713850000000 }, // 订单详情场景：用户关闭/确认引导的时间戳
 *   "strategy":   { "dismissTime": 1713860000000 }  // 抢票攻略场景：用户关闭/确认引导的时间戳
 * }
 * 注意：三端（show-h5 / show-main-mrn / seat-h5）共享同一 key，sceneType 命名须保持一致
 */
var STORE_KEY = '_myshow_notification_guide_dismiss';
var DEFAULT_INTERVAL_MS = 24 * 60 * 60 * 1000; // 默认 24h

// ─── KNB 存储工具（与 MRN 端逻辑对齐）────────────────────────────────────────────
/**
 * 从 KNB 本地存储读取频次控制数据
 * level: 1 = 设备级存储，与 MRN 端 setDismissData 保持一致
 */
function getDismissData() {
  return new Promise(function (resolve) {
    KNB.getStorage({
      key: STORE_KEY,
      success: function success(res) {
        try {
          var value = res && res.value;
          if (!value) {
            resolve({});
            return;
          }
          var parsed = typeof value === 'string' ? JSON.parse(value) : value;
          resolve(parsed && _typeof(parsed) === 'object' ? parsed : {});
        } catch (_) {
          resolve({});
        }
      },
      fail: function fail() {
        resolve({});
      }
    });
  });
}

/**
 * 将频次控制数据写入 KNB 本地存储
 * level: 1 = 设备级存储，App 重装后数据仍保留，与 MRN 端保持一致
 */
function setDismissData(data) {
  return new Promise(function (resolve) {
    KNB.setStorage({
      key: STORE_KEY,
      value: JSON.stringify(data),
      level: 1,
      success: function success() {
        resolve();
      },
      fail: function fail() {
        // 存储失败静默忽略，冷却是体验优化，不影响主流程
        resolve();
      }
    });
  });
}

/**
 * 统一获取通知引导 Lion 配置
 * notificationGuide 是 maoyan-show-m-web.frontEnd.config.projectDetailPageConfig 对象下的字段
 * 与项目其他页面（ticket-level、ticket-level-v2 等）获取 projectDetailPageConfig 的方式保持一致
 * 配置结构（示例）：
 * {
 *   enabled: true,           // 总开关
 *   wishEnabled: true,       // 许愿场景开关
 *   wishControlMs: 86400000, // 许愿场景冷却时长(ms)
 *   presetEnabled: true,     // 预约场景开关
 *   presetControlMs: 86400000,
 *   outOfStockEnabled: true, // 缺货登记场景开关
 *   outOfStockControlMs: 86400000,
 *   orderEnabled: true,      // 订单详情场景开关（默认 true）
 *   orderControlMs: 86400000, // 订单详情场景冷却时长(ms)，默认 24h
 *   strategyEnabled: true,   // 抢票攻略场景开关（默认 true）
 *   strategyControlMs: 86400000, // 抢票攻略场景冷却时长(ms)，默认 24h
 * }
 * @returns {Promise<object>} Lion 配置对象，获取失败返回 {}（默认展示，与 seat-h5/mrn 端行为一致）
 */
export function getNotificationGuideConfig() {
  return _getNotificationGuideConfig.apply(this, arguments);
}

/**
 * 检查系统通知权限（仅查询，不弹系统授权弹窗）
 * 仅在猫眼 / 美团 / 大众点评 APP 内有效（文档 2.2 涉及渠道）
 * 其他渠道（格瓦拉、QQWALLET 等）不支持此能力，直接返回 true（视为已授权，不出引导弹窗）
 * @returns {Promise<boolean>} true = 已授权，false = 未授权或查询失败
 */
function _getNotificationGuideConfig() {
  _getNotificationGuideConfig = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var projectDetailPageConfig;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.prev = 0;
          _context.next = 3;
          return getLionConfig('projectDetailPageConfig');
        case 3:
          projectDetailPageConfig = _context.sent;
          return _context.abrupt("return", projectDetailPageConfig && projectDetailPageConfig.notificationGuide || {});
        case 7:
          _context.prev = 7;
          _context.t0 = _context["catch"](0);
          return _context.abrupt("return", {});
        case 10:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[0, 7]]);
  }));
  return _getNotificationGuideConfig.apply(this, arguments);
}
export function checkNotificationPermission() {
  var _Drip$UA;
  // 仅猫眼 / 美团 / 大众点评 三个渠道支持通知权限引导（文档 2.2）
  // 注意：Drip.UA.isNative 包含了格瓦拉(GEWARA)，不够严谨
  var isSupportedChannel = Drip.UA.IS_MAOYAN || Drip.UA.IS_MEITUAN || Drip.UA.IS_DIANPING;
  if (!isSupportedChannel) {
    // 非支持渠道返回 true：视为已授权，不出引导弹窗
    return Promise.resolve(true);
  }
  var isIOS = ((_Drip$UA = Drip.UA) === null || _Drip$UA === void 0 ? void 0 : _Drip$UA.osName) === 'ios';
  return new Promise(function (resolve) {
    // KNB 内部实现不会同步 throw，失败统一走 fail 回调，无需 try-catch
    KNB.requestPermission({
      type: 'notification',
      readonly: true,
      success: function success() {
        resolve(true);
      },
      fail: function fail(err) {
        if (isIOS && err && err.errorCode === 546) {
          /*
            errorCode 546：APP 从未弹出过系统授权弹窗，代表系统设置页面不会出现通知这一行，
            这种情况不需要跳转到系统设置页（不出引导弹窗），故返回 true。
            仅 iOS 存在此场景：猫眼 iOS 首次下载打开后，会先出自定义引导弹窗，
            点击引导弹窗里的按钮才会弹出系统授权弹窗。
            猫眼 Android 首次下载打开后，系统授权弹窗一定已弹出，设置页一定有通知这一行，
            无需此判断。
          */
          resolve(true);
          return;
        }
        resolve(false);
      }
    });
  });
}

/**
 * 记录用户关闭引导弹窗/横幅，写入冷却时间戳
 * 存储失败静默忽略：冷却本身是体验优化，不影响主业务，调用方无需感知写入结果
 * @param {string} sceneType - 'wish' | 'preset' | 'outOfStock' | 'order' | 'strategy'
 */
export function recordDismiss(_x) {
  return _recordDismiss.apply(this, arguments);
}

/**
 * 判断当前 sceneType 是否在冷却期内
 * 读取失败返回 false（视为不在冷却期，可展示引导），与 seat-h5/mrn 端行为一致
 * @param {string} sceneType - 'wish' | 'preset' | 'outOfStock' | 'order' | 'strategy'
 * @param {number} [intervalMs] - 冷却时长（ms），默认 24h
 * @returns {Promise<boolean>} true = 冷却期内（不弹），false = 可弹出
 */
function _recordDismiss() {
  _recordDismiss = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(sceneType) {
    var data;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _context2.prev = 0;
          _context2.next = 3;
          return getDismissData();
        case 3:
          data = _context2.sent;
          data[sceneType] = {
            dismissTime: Date.now()
          };
          _context2.next = 7;
          return setDismissData(data);
        case 7:
          _context2.next = 11;
          break;
        case 9:
          _context2.prev = 9;
          _context2.t0 = _context2["catch"](0);
        case 11:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[0, 9]]);
  }));
  return _recordDismiss.apply(this, arguments);
}
export function isInCoolingPeriod(_x2) {
  return _isInCoolingPeriod.apply(this, arguments);
}

/**
 * 跳转系统设置页（通知权限开关页）
 * 全部方案失败时返回 false，由调用方展示 Toast 兜底
 *
 * 注意（来自 KNB 官方文档）：
 * - KNB.openAppSetting：iOS + Android 均有效，正确调用方式为 KNB.use('openAppSetting', {...})
 * - KNB.openPage({ url: 'app-settings:' })：仅 iOS 有效，Android 不可用
 * - location.href = 'app-settings:'：仅 iOS 有效，Android 不可用
 *
 * 此方法不阻塞页面主流程：返回 Promise，await 仅用于获取回调结果后决定是否显示 Toast，
 * 属于微任务挂起，不占用 JS 主线程。
 *
 * @returns {Promise<boolean>} true = 跳转已触发，false = 全部失败
 */
function _isInCoolingPeriod() {
  _isInCoolingPeriod = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(sceneType) {
    var intervalMs,
      data,
      record,
      _args3 = arguments;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          intervalMs = _args3.length > 1 && _args3[1] !== undefined ? _args3[1] : DEFAULT_INTERVAL_MS;
          _context3.prev = 1;
          _context3.next = 4;
          return getDismissData();
        case 4:
          data = _context3.sent;
          record = data[sceneType];
          if (!(!record || !record.dismissTime)) {
            _context3.next = 8;
            break;
          }
          return _context3.abrupt("return", false);
        case 8:
          return _context3.abrupt("return", Date.now() - record.dismissTime < intervalMs);
        case 11:
          _context3.prev = 11;
          _context3.t0 = _context3["catch"](1);
          return _context3.abrupt("return", false);
        case 14:
        case "end":
          return _context3.stop();
      }
    }, _callee3, null, [[1, 11]]);
  }));
  return _isInCoolingPeriod.apply(this, arguments);
}
export function openAppSetting() {
  var _Drip$constant, _Drip$constant$uaInfo;
  var isIOS = (Drip === null || Drip === void 0 ? void 0 : (_Drip$constant = Drip.constant) === null || _Drip$constant === void 0 ? void 0 : (_Drip$constant$uaInfo = _Drip$constant.uaInfo) === null || _Drip$constant$uaInfo === void 0 ? void 0 : _Drip$constant$uaInfo.osName) === 'ios';
  return new Promise(function (resolve) {
    // KNB 内部实现不会同步 throw，失败统一走 fail 回调，无需 try-catch
    KNB.use('openAppSetting', {
      success: function success() {
        resolve(true);
      },
      fail: function fail() {
        // Android 上 app-settings: scheme 不可用，第一层失败直接提示用户手动操作
        if (!isIOS) {
          resolve(false);
          return;
        }
        // iOS 第二层：KNB.openPage 经 Native Bridge openScheme 发起跳转
        // app-settings: 不是 http/https，KNB 会直接透传给 Native 处理
        KNB.openPage({
          url: 'app-settings:',
          success: function success() {
            resolve(true);
          },
          fail: function fail() {
            // iOS 第三层：WebView 直接跳转兜底，location.href 赋值不会抛异常
            location.href = 'app-settings:';
            resolve(true);
          }
        });
      }
    });
  });
}